import { Box, Chip, IconButton, Link, Tooltip, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { _date, formatMoney } from "../../../utils/helpers";

function statusColor(status) {
    const s = String(status || "").toLowerCase();
    if (s === "processed") return "success";
    if (s === "created") return "info";
    if (s === "refunded" || s === "canceled" || s === "cancelled") return "warning";
    if (s === "failed") return "error";
    return "default";
}

function methodColor(method) {
    const m = String(method || "").toLowerCase();
    if (m === "card") return "primary";
    if (m === "cash") return "secondary";
    return "default";
}

export const TransactionColumns = ({ onOpenOrder } = {}) => [
    {
        id: "id",
        label: "#",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: "center", fontWeight: 500 }}>
                #{row.id}
            </Typography>
        ),
    },
    {
        id: "customer",
        label: "Customer",
        render: (value, row) => (
            <Box sx={{ textAlign: "center" }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {row.user?.full_name || row.order?.customer_name || "—"}
                </Typography>
                {row.user?.email ? (
                    <Typography variant="caption" color="text.secondary">{row.user.email}</Typography>
                ) : null}
            </Box>
        ),
    },
    {
        id: "method",
        label: "Method",
        render: (value, row) => (
            <Box sx={{ textAlign: "center" }}>
                <Chip
                    size="small"
                    color={methodColor(row.method)}
                    label={row.method || "—"}
                />
            </Box>
        ),
    },
    {
        id: "amount",
        label: "Amount",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: "center", fontWeight: 500 }}>
                {formatMoney(Number(row.amount || 0))}
            </Typography>
        ),
    },
    {
        id: "promocode",
        label: "Promocode",
        render: (value, row) => {
            const promo = row.order?.promocode;
            if (!promo?.code) {
                return (
                    <Typography variant="body2" sx={{ textAlign: "center" }} color="text.secondary">
                        —
                    </Typography>
                );
            }
            return (
                <Box sx={{ textAlign: "center" }}>
                    <Chip size="small" variant="outlined" label={promo.code} />
                    {promo.discount != null ? (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {promo.discount}%
                        </Typography>
                    ) : null}
                </Box>
            );
        },
    },
    {
        id: "discount",
        label: "Discount",
        render: (value, row) => {
            const amount = Number(row.order?.discount_amount || 0);
            return (
                <Typography variant="body2" sx={{ textAlign: "center", fontWeight: amount > 0 ? 500 : 400 }}>
                    {amount > 0 ? formatMoney(amount) : "—"}
                </Typography>
            );
        },
    },
    {
        id: "status",
        label: "Status",
        render: (value, row) => (
            <Box sx={{ textAlign: "center" }}>
                <Tooltip title={row.message || ""}>
                    <Chip size="small" color={statusColor(row.status)} label={row.status || "—"} />
                </Tooltip>
            </Box>
        ),
    },
    {
        id: "order_id",
        label: "Order",
        render: (value, row) => (
            <Box sx={{ textAlign: "center" }}>
                {row.order_id ? (
                    <Link
                        component="button"
                        type="button"
                        variant="body2"
                        underline="hover"
                        onClick={() => onOpenOrder?.(row)}
                        sx={{ fontWeight: 500 }}
                    >
                        #{row.order_id}
                    </Link>
                ) : (
                    <Typography variant="body2">—</Typography>
                )}
            </Box>
        ),
    },
    {
        id: "transaction_id",
        label: "Gateway Order ID",
        render: (value, row) => (
            <Tooltip title={row.transaction_id || ""}>
                <Typography
                    variant="caption"
                    sx={{
                        textAlign: "center",
                        display: "block",
                        fontFamily: "monospace",
                        maxWidth: 160,
                        mx: "auto",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {row.transaction_id || "—"}
                </Typography>
            </Tooltip>
        ),
    },
    {
        id: "created_at",
        label: "Date",
        render: (value, row) => (
            <Typography variant="body2" sx={{ textAlign: "center" }}>
                {row.created_at ? _date(row.created_at, { time: true, long: false }) : "—"}
            </Typography>
        ),
    },
    {
        id: "details",
        label: "",
        render: (value, row) => (
            <Box sx={{ textAlign: "center" }}>
                <Tooltip title="Order details">
                    <IconButton size="small" onClick={() => onOpenOrder?.(row)} aria-label="Order details">
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
    },
];
